import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { RESOURCE_TYPE, DATE_FORMAT } from "../../constants";
import styles from "./AsnOverviewReport.module.css";
import "../../cssModules/switch.css";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import { getAsnOverviewReport } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { downloadCsv } from "../../utils/utils";
import { buildErrorMessage } from "../../apis/calls";
import LineDropdownSelector from "../LineDropdownSelector/LineDropdownSelector";
import { CustomDatePicker } from "../FormComponents";
import clsx from "clsx";

const AsnOverviewReport = ({
  setHasActiveEdits,
  hasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;

  const { showInternalError } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const history = useHistory();

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [asnCount, setAsnCount] = useState(0);
  const [line] = usePlantAndLinesStore().lineState;
  const [fromDate, setfromDate] = useState(moment().format(DATE_FORMAT.ISO));
  const [toDate, settoDate] = useState(moment().format(DATE_FORMAT.ISO));
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const setCount = (count) => {
    setAsnCount(count);
  };
  const fixedColumns = [
    {
      width: 150,
      title: "ASN",
      field: "asn",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "VC",
      field: "vc",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 220,
      title: "ParentSerial Number",
      field: "parentSerialNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Status",
      field: "status",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: "ChildSerial Number",
      field: "childSerialNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 230,
      title: "Child Status",
      field: "childStatus",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: "Part Number",
      field: "childPartNumber",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Aggregate",
      field: "type",
      enableSearch: true,
      enableFilter: true,
    },
  ].filter((c) => c);



  const getAsnOverviewreportdata = async () => {
    if (plant.value) {
      setRowsData((prevState) => ({ ...prevState, loading: true }));
      try {
        let response = await getAsnOverviewReport(
          plant.value,
          line.value,
          toDate,
          fromDate
        );
        setRowsData({
          loading: false,
          data: response.data,
          filteredData: response.data,
        });
      } catch (err) {
        console.log(err);
        showInternalError({ error: err });
      }
    }
  };

  const prepareJsonHeaderAndValueFormats = (aggregatePartNames) => {
    const vcAggregateHeaderAndValueFormats = aggregatePartNames.map(
      (partName) => ({
        label: partName + " Status",
        value: (row) =>
          row.materialGroupClusters.filter(
            (group) => group.name === partName
          )[0]?.materials[0]?.aggregateStatus || "unavailable",
      })
    );

    const vcHeaderAndValueFormats = [
      {
        label: "ASN",
        value: (row) => row.asn,
      },
      {
        label: "VC",
        value: (row) => row.vc,
      },
      {
        label: "ParentSerial Number",
        value: (row) => row.parentSerialNumber,
      },
      {
        label: "Status",
        value: (row) => row.status,
      },
      {
        label: "ChildSerial Number",
        value: (row) => row.childSerialNumber,
      },
      {
        label: "Child Status",
        value: (row) => row.childStatus,
      },
      {
        label: "Part Number",
        value: (row) => row.childPartNumber,
      },
      {
        label: "Aggregate",
        value: (row) => row.type,
      },
    ].filter((c) => c);

    return [...vcHeaderAndValueFormats, ...vcAggregateHeaderAndValueFormats];
  };

  const generateAndDownloadAsnOverviewCsvReport = (filteredData) => {
    try {
      if (!rowsData.loading) {
        const dateValue = moment().format(DATE_FORMAT.ISO);

        const jsonData = filteredData;

        const aggregatePartNames = [];

        const { parseAsync } = require("json2csv");
        const fields = prepareJsonHeaderAndValueFormats(aggregatePartNames);
        const opts = { fields };
        parseAsync(jsonData, opts)
          .then((csv) => {
            const exportedFilename =
              "AsnOverviewReport_" +
              plant.label +
              "_" +
              line.label +
              "_" +
              dateValue +
              ".csv";
            downloadCsv(csv, exportedFilename);
          })
          .catch((error) =>
            console.error(
              "Failed to download AsnOverview report, error:" +
                buildErrorMessage(error)
            )
          );
      }
    } catch (error) {
      console.error(
        "Failed to download AsnOverview report, error:" +
          buildErrorMessage(error)
      );
    }
  };

  const handleReset = () => {
    const today = moment().format(DATE_FORMAT.ISO);
    setfromDate(today);
    settoDate(today);
    setShouldFetchData(true);
  };

  useEffect(() => {
    if (shouldFetchData) {
      getAsnOverviewreportdata();
      setShouldFetchData(false); 
    }
  }, [fromDate, toDate, shouldFetchData]);

  useEffect(() => {
    handleCountChange(asnCount);
  }, [asnCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    getAsnOverviewreportdata();
  }, [plant.value, line.value]);

  return (
    <>
      <div className={styles.RgtTools}>
        <div className={styles.eAgsnToolbar}>
          <div className={styles.selectorSmall}>
            <span className={styles.captionSmall}>Select Line</span>
          </div>
          <div className={styles.selectorBt}>
            <LineDropdownSelector />
          </div>
          <div className={styles.selectorSmall}>
            <label className={styles.captionSmall}>Start date</label>
            <CustomDatePicker
              isMandatory
              autoOk
              inputVariant="outlined"
              value={fromDate}
              onChange={(date) => {
                const newFromDate = date.format(DATE_FORMAT.ISO);
                setfromDate(newFromDate);
                if (moment(newFromDate).isAfter(toDate)) {
                  settoDate(newFromDate);
                }
              }}
              inputProps={{
                className: clsx(styles.select, styles.dateInput),
              }}
              emptyLabel="dd/mm/yyyy"
              minDate={moment(1)}
              format="DD/MM/yyyy"
              data-testid="date-picker-div"
            />
          </div>
          <div className={styles.selectorSmall}>
            <label className={styles.captionSmall}>End date</label>
            <CustomDatePicker
              isMandatory
              autoOk
              inputVariant="outlined"
              value={toDate}
              onChange={(date) => {
                settoDate(date.format(DATE_FORMAT.ISO));
              }}
              inputProps={{
                className: clsx(styles.select, styles.dateInput),
              }}
              emptyLabel="dd/mm/yyyy"
              minDate={moment(fromDate)}
              maxDate={moment(fromDate).add(5, "days")}
              format="DD/MM/yyyy"
              data-testid="date-picker-div"
            />
          </div>
          <button className={styles.submit} onClick={getAsnOverviewreportdata}>
            Submit
          </button>
          <button className={styles.reset} onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            secondaryActions={[
              {
                name: "DOWNLOAD CSV",
                getFilteredData: true,
                shouldEnable: () => true,
                actionFn: (filteredData) =>
                  generateAndDownloadAsnOverviewCsvReport(filteredData),
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};

AsnOverviewReport.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  AsnOverviewReport,
  false,
  RESOURCE_TYPE.CHASSIS
);
